import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip, Row, Select } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import fileDownload from 'js-file-download';

import styles from '../styles';
import {
  errorNotification,
  errorNotificationOnBlob,
  successNotification,
} from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';
import { clearQualityChecks, getQualityChecks } from '../Actions';
import { currentFinancialYear, financialYears } from '../financial-years';

const QualityCheckList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const qualityChecks = useSelector(state => state.qualityChecks);

  const [Year, SetYear] = useState(currentFinancialYear);

  useEffect(() => {
    dispatch(getQualityChecks({ year: Year }));

    return () => {
      dispatch(clearQualityChecks());
    };
  }, [Year]);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/quality-checks/${id}`);
      successNotification('Quality check deleted');
      dispatch(getQualityChecks());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const onFileDownload = useCallback(async id => {
    try {
      const { data } = await Api.getFile(`/quality-checks/download/${id}`);
      fileDownload(data, `quality_check-${id}.pdf`);
      successNotification('File downloaded');
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  }, []);

  const downloadColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="File">
          <DownloadOutlined
            className={classes.tableIcon}
            onClick={() => onFileDownload(record.id)}
          />
        </Tooltip>
      ),
    []
  );

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateQualityCheck) ? (
            <Tooltip placement="bottom" title="Update">
              <Link
                to={`/qualitycheck/update/${record.storeInwardId}/${record.id}`}
              >
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteQualityCheck) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const viewColumn = useMemo(
    () => record =>
      (
        <Tooltip placement="bottom" title="View">
          <Link to={`/qualitycheck/view/${record.storeInwardId}/${record.id}`}>
            <FileOutlined className={classes.tableIcon} />
          </Link>
        </Tooltip>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'QC #',
        dataIndex: 'qcNo',
        sorter: sorter('qcNo'),
        onFilter: onFilter('qcNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'QC Date',
        dataIndex: 'qcDate',
        sorter: dateSorter('qcDate'),
        onFilter: onDateFilter('qcDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: qcdate =>
          qcdate ? moment(qcdate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
        sorter: sorter('bpoNo'),
        onFilter: onFilter('bpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO Date',
        dataIndex: 'rpoDate',
        sorter: dateSorter('rpoDate'),
        onFilter: onDateFilter('rpoDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: rpoDate =>
          rpoDate ? moment(rpoDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Inward #',
        dataIndex: 'inwardNo',
        sorter: sorter('inwardNo'),
        onFilter: onFilter('inwardNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Inward Date',
        dataIndex: 'inwardDate',
        sorter: dateSorter('inwardDate'),
        onFilter: onDateFilter('inwardDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: inwardDate =>
          inwardDate ? moment(inwardDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Products',
        dataIndex: 'products',
        sorter: sorter('products'),
        onFilter: onFilter('products'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Fabrics',
        dataIndex: 'fabrics',
        sorter: sorter('fabrics'),
        onFilter: onFilter('fabrics'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Styles',
        dataIndex: 'styles',
        sorter: sorter('styles'),
        onFilter: onFilter('styles'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Items',
        dataIndex: 'items',
        sorter: sorter('items'),
        onFilter: onFilter('items'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Accessories',
        dataIndex: 'accessories',
        sorter: sorter('accessories'),
        onFilter: onFilter('accessories'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
      },
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      },
    ];

    if (permissions.includes(constants.permissions.updateQualityCheck)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteQualityCheck)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'View',
        render: viewColumn,
        fixed: 'right',
        width: '2%',
      },
      {
        title: 'Download',
        render: downloadColumn,
        fixed: 'right',
        width: '3%',
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 3000, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Quality Check"
        moduleLink="/qualitycheck/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Row>
          <Select
            value={Year}
            style={{ width: 150 }}
            onChange={value => SetYear(value)}
          >
            {financialYears.map(el => (
              <Select.Option key={el} value={el}>
                {el}
              </Select.Option>
            ))}
          </Select>
        </Row>
        <br />
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={qualityChecks}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

QualityCheckList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(QualityCheckList);
